import React, { useState, useEffect, useContext } from "react";
import { X, CreditCard, Plus, Minus } from "lucide-react";
import Cookies from "js-cookie";
import { SessionContext } from "../../context/sessionContext";
import { getOverageRate } from "../../assets/data/pricingData";

const ManagePaymentModal = ({ isOpen, onClose }) => {
  const [credits, setCredits] = useState(20000);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [planData, setPlanData] = useState(null);
  const [fetchingData, setFetchingData] = useState(true);
  const { session, setSession } = useContext(SessionContext);

  // Mock payment method - in real app, fetch from backend
  const paymentMethod = {
    last4: "3456",
    brand: "Visa",
    expiry: "12/25",
  };

  // Fetch user plan data
  useEffect(() => {
    if (isOpen) {
      fetchPlanData();
    }
  }, [isOpen]);

  const fetchPlanData = async () => {
    setFetchingData(true);
    try {
      const token = Cookies.get("token");
      if (!token) {
        setError("Authentication required");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/project/getuserPlanAndProject`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          credentials: "include",
        },
      );

      const data = await response.json();
      if (data.status === "success") {
        setPlanData(data);
      }
    } catch (err) {
      console.error("Error fetching plan data:", err);
      setError("Failed to load plan data");
    } finally {
      setFetchingData(false);
    }
  };

  const getCurrentPlanPrice = () =>
    getOverageRate(planData?.planDetails?.plan_name);

  const calculateTotalCost = () => {
    const pricePerRU = getCurrentPlanPrice();
    return (credits * pricePerRU).toFixed(2);
  };

  const formatCredits = (num) => {
    return new Intl.NumberFormat("en-US").format(num);
  };

  const handleIncrement = (amount) => {
    setCredits((prev) => Math.max(0, prev + amount));
    setError("");
  };

  const handleDecrement = (amount) => {
    setCredits((prev) => Math.max(0, prev - amount));
    setError("");
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/,/g, "");
    if (value === "" || /^\d+$/.test(value)) {
      setCredits(value === "" ? 0 : parseInt(value));
      setError("");
    }
  };

  const handlePayment = async () => {
    if (!credits || credits <= 0) {
      setError("Please enter a valid credit amount");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = Cookies.get("token");

      if (!token) {
        window.location.href = "/log-in";
        return;
      }
      localStorage.setItem("creditPurchase", "true");
      localStorage.setItem(
        "paymentContext",
        JSON.stringify({
          source: "ADD_CREDITS",
          newScore: credits,
        }),
      );

      localStorage.setItem(
        "purchaseCredits",
        JSON.stringify({
          credits: parseInt(credits),
          amount: parseFloat(calculateTotalCost()),
        }),
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          credentials: "include",
          body: JSON.stringify({
            planName: "Credit Purchase",
            amount: parseFloat(calculateTotalCost()),
            userId: session?.user?.user_id || session?.user?.id,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok || !data.url) {
        throw new Error(data.message || "Failed to create payment session");
      }
      window.location.href = data.url;
    } catch (err) {
      console.error("Error processing payment:", err);
      setError("Failed to process payment. Please try again.");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const pricePerRU = getCurrentPlanPrice();
  const totalCost = calculateTotalCost();

  return (
    <div className="collaborator-modal-overlay">
      <div className="collaborator-modal" style={{ maxWidth: "550px" }}>
        {/* Header */}
        <div className="collaborator-modal-header">
          <div>
            <h2 className="collaborator-modal-title">Purchase Credits</h2>
            <p className="collaborator-modal-subtitle">
              {fetchingData ? (
                "Loading..."
              ) : planData ? (
                <>
                  Plan:{" "}
                  <span style={{ color: "#3b82f6", fontWeight: 600 }}>
                    {planData.planDetails?.plan_name}
                  </span>{" "}
                  •
                  <span
                    style={{
                      color: "#10b981",
                      fontWeight: 600,
                      marginLeft: "0.5rem",
                    }}
                  >
                    {formatCredits(planData.planDetails?.credits_score || 0)}{" "}
                    Credits Available
                  </span>
                </>
              ) : (
                "Add credits to your account"
              )}
            </p>
          </div>
          <button onClick={onClose} className="collaborator-modal-close">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="collaborator-modal-content">
          {/* Credit Calculator */}
          <div
            style={{
              backgroundColor: "rgba(59, 130, 246, 0.05)",
              border: "1px solid rgba(59, 130, 246, 0.2)",
              borderRadius: "12px",
              padding: "1.5rem",
              marginBottom: "1.5rem",
            }}
          >
            <label
              className="collaborator-input-label"
              style={{ marginBottom: "1rem", display: "block" }}
            >
              Research Units (RU) to Purchase
            </label>

            {/* Credit Input with +/- Buttons */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <button
                onClick={() => handleDecrement(1000)}
                disabled={loading}
                style={{
                  width: "44px",
                  height: "44px",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "8px",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                  opacity: loading ? 0.5 : 1,
                }}
                onMouseOver={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(239, 68, 68, 0.2)";
                    e.currentTarget.style.borderColor = "#ef4444";
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.borderColor =
                      "rgba(255, 255, 255, 0.2)";
                  }
                }}
              >
                <Minus size={20} />
              </button>

              <input
                type="text"
                value={formatCredits(credits)}
                onChange={handleInputChange}
                disabled={loading}
                className="collaborator-input"
                style={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  cursor: loading ? "not-allowed" : "text",
                }}
              />

              <button
                onClick={() => handleIncrement(1000)}
                disabled={loading}
                style={{
                  width: "44px",
                  height: "44px",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "8px",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                  opacity: loading ? 0.5 : 1,
                }}
                onMouseOver={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(16, 185, 129, 0.2)";
                    e.currentTarget.style.borderColor = "#10b981";
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.borderColor =
                      "rgba(255, 255, 255, 0.2)";
                  }
                }}
              >
                <Plus size={20} />
              </button>
            </div>

            {/* Quick Amount Buttons */}
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                marginBottom: "1.5rem",
                flexWrap: "wrap",
              }}
            >
              {[10000, 25000, 50000].map((amount) => (
                <button
                  key={amount}
                  onClick={() => {
                    setCredits(amount);
                    setError("");
                  }}
                  disabled={loading}
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor:
                      credits === amount
                        ? "rgba(59, 130, 246, 0.3)"
                        : "rgba(255, 255, 255, 0.05)",
                    border:
                      credits === amount
                        ? "1px solid #3b82f6"
                        : "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: credits === amount ? "#3b82f6" : "#9ca3af",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    cursor: loading ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    opacity: loading ? 0.5 : 1,
                  }}
                  onMouseOver={(e) => {
                    if (credits !== amount && !loading) {
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 255, 255, 0.08)";
                      e.currentTarget.style.borderColor =
                        "rgba(255, 255, 255, 0.2)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (credits !== amount && !loading) {
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 255, 255, 0.05)";
                      e.currentTarget.style.borderColor =
                        "rgba(255, 255, 255, 0.1)";
                    }
                  }}
                >
                  {formatCredits(amount)}
                </button>
              ))}
            </div>

            {/* Pricing Breakdown */}
            <div
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                borderRadius: "8px",
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                  Overage Rate ({planData?.planDetails?.plan_name || "Standard"}{" "}
                  Plan)
                </span>
                <span
                  style={{
                    color: "#ffffff",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  ${pricePerRU.toFixed(2)} per RU
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                  Research Units
                </span>
                <span
                  style={{
                    color: "#ffffff",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {formatCredits(credits)} RU
                </span>
              </div>
              <div
                style={{
                  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                  paddingTop: "0.75rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    color: "#ffffff",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Total Cost
                </span>
                <span
                  style={{
                    color: "#3b82f6",
                    fontSize: "1.5rem",
                    fontWeight: 700,
                  }}
                >
                  ${totalCost}
                </span>
              </div>
            </div>
          </div>

          {error && (
            <p className="collaborator-error" style={{ marginBottom: "1rem" }}>
              {error}
            </p>
          )}

          {/* Payment Method Section
          <div style={{ marginBottom: '1.5rem' }}>
            <label className="collaborator-input-label" style={{ marginBottom: '1rem' }}>
              Payment Method
            </label>
            
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '1.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(59, 130, 246, 0.2)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#3b82f6'
                }}>
                  <CreditCard size={24} />
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem'
                  }}>
                    {paymentMethod.brand} •••• {paymentMethod.last4}
                  </div>
                  <div style={{
                    color: '#9ca3af',
                    fontSize: '0.875rem'
                  }}>
                    Expires {paymentMethod.expiry}
                  </div>
                </div>

                <button
                  disabled={loading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '0.875rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: loading ? 0.5 : 1
                  }}
                  onMouseOver={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    }
                  }}
                >
                  Change
                </button>
              </div>
            </div>
          </div> */}

          {/* Info Box */}
          <div
            style={{
              padding: "1rem",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.2)",
              borderRadius: "8px",
              marginBottom: "1.5rem",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "0.875rem",
                color: "#94a3b8",
                lineHeight: "1.5",
              }}
            >
              💡 Credits will be added to your account immediately after payment
              confirmation. Overage pricing applies to credits purchased beyond
              your plan allocation.
            </p>
          </div>

          {/* Pay Button */}
          <button
            onClick={handlePayment}
            disabled={loading || !credits || credits <= 0}
            style={{
              width: "100%",
              padding: "1rem",
              backgroundColor: loading || !credits ? "#374151" : "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "1rem",
              fontWeight: 600,
              cursor: loading || !credits ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => {
              if (!loading && credits > 0) {
                e.target.style.backgroundColor = "#2563eb";
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.4)";
              }
            }}
            onMouseOut={(e) => {
              if (!loading && credits > 0) {
                e.target.style.backgroundColor = "#3b82f6";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }
            }}
          >
            <CreditCard size={20} />
            {loading ? "Processing..." : `Pay $${totalCost}`}
          </button>
        </div>

        {/* Footer */}
        <div className="collaborator-modal-footer">
          <button
            onClick={onClose}
            disabled={loading}
            className="collaborator-cancel-button"
            style={{ opacity: loading ? 0.5 : 1 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManagePaymentModal;
